import { RoutingError, type Route } from '@metro/routing'
import type { OfflineRouteInputs } from './offlineRouteSearch'
import type { OfflineSearchRequest, OfflineSearchResult } from './offlineSearchProtocol'
import type { OfflineSearchRunner } from './offlineSearchRunner'

/** The part of `Worker` the runner uses — a seam for tests. */
export interface OfflineSearchWorker {
  postMessage(message: OfflineSearchRequest): void
  onmessage: ((event: MessageEvent<OfflineSearchResult>) => void) | null
  onerror: ((event: ErrorEvent) => void) | null
  terminate(): void
}

interface Waiter {
  readonly resolve: (routes: Route[]) => void
  readonly reject: (err: Error) => void
}

/**
 * The offline search in a Web Worker: the graph is built there once from
 * `inputs`, each `run` posts one search and resolves with its reply. A worker
 * that fails (script error, crash) rejects every pending search, so the
 * adapter reports the failure instead of waiting forever; the next `run`
 * starts a fresh worker. `dispose` terminates the worker — a later `run`
 * (React StrictMode re-runs effects on the same adapter) starts a new one.
 */
export function createWorkerOfflineSearchRunner(
  inputs: OfflineRouteInputs,
  makeWorker: () => OfflineSearchWorker = defaultWorker,
): OfflineSearchRunner {
  const pending = new Map<number, Waiter>()
  let nextId = 0
  let worker: OfflineSearchWorker | null = null

  const stop = (err: Error): void => {
    worker?.terminate()
    worker = null
    for (const { reject } of pending.values()) reject(err)
    pending.clear()
  }
  const start = (): OfflineSearchWorker => {
    const started = makeWorker()
    started.onmessage = (event) => {
      const reply = event.data
      const waiter = pending.get(reply.id)
      if (waiter === undefined) return
      pending.delete(reply.id)
      if ('routes' in reply) waiter.resolve(reply.routes)
      else if (reply.routingErrorCode !== undefined)
        waiter.reject(new RoutingError(reply.routingErrorCode))
      else waiter.reject(new Error(reply.message))
    }
    started.onerror = (event) => {
      if (worker === started) stop(new Error(`offline route worker failed: ${event.message}`))
    }
    started.postMessage({ kind: 'init', inputs })
    return started
  }
  worker = start()

  return {
    run: (from, to) =>
      new Promise<Route[]>((resolve, reject) => {
        worker ??= start()
        const id = nextId++
        pending.set(id, { resolve, reject })
        worker.postMessage({ kind: 'search', id, from, to })
      }),
    dispose: () => stop(new Error('offline route worker disposed')),
  }
}

function defaultWorker(): OfflineSearchWorker {
  return new Worker(new URL('./offlineRouteWorker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as OfflineSearchWorker
}
